import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@material-ui/icons";
import { Box, Grid, Divider, Link } from "@material-ui/core";
import {
  AUTH_ROOT_ROUTE,
  PHONE_VERIFICATION_ROUTE,
} from "routes/auth/authPaths";
import useStyles from "./accountStyles";
import useSecurity from "./useSecurity";
import translate from "../../../i18n/translate";
import translateIntl from "../../../i18n/translateIntl";
import {
  SrAlert,
  SrButton,
  SrLabel,
  Typography,
  SrInputPassword,
} from "../../common";
import { validations } from "utils";

const Security = () => {
  const classes = useStyles();
  const { user } = useSelector(({ auth }) => auth);
  const { error, changePassword } = useSecurity();

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required(translate("common.requiredField")),
    newPassword: validations.password.required(
      translate("common.requiredField")
    ),
    confirmPassword: validations.password
      .oneOf([Yup.ref("newPassword"), null], translate("common.passwordMatch"))
      .required(translate("common.requiredField")),
  });

  return (
    user && (
      <Formik
        initialValues={initialValues}
        onSubmit={changePassword}
        validationSchema={validationSchema}
      >
        {({
          errors,
          touched,
          values,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} container alignItems="center">
                <SrLabel name="currentPassword">
                  {translate("common.currentPassword")}
                </SrLabel>
              </Grid>
              <Grid item xs={12} md={8}>
                <SrInputPassword
                  name="currentPassword"
                  value={values.currentPassword}
                  autoComplete="current-password"
                  error={touched.currentPassword && !!errors.currentPassword}
                  fullWidth
                  helperText={touched.currentPassword && errors.currentPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={translateIntl(
                    "account.security.enterPrePassword"
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4} container alignItems="center">
                <SrLabel name="newPassword">
                  {translate("common.newPassword")}
                </SrLabel>
              </Grid>
              <Grid item xs={12} md={4}>
                <SrInputPassword
                  name="newPassword"
                  value={values.newPassword}
                  autoComplete="new-password"
                  error={touched.newPassword && !!errors.newPassword}
                  fullWidth
                  helperText={touched.newPassword && errors.newPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={translateIntl(
                    "account.security.enterNewPassword"
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SrInputPassword
                  name="confirmPassword"
                  value={values.confirmPassword}
                  autoComplete="new-password"
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  fullWidth
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={translateIntl("common.confirmPassword")}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={4}>
                <SrLabel>{translate("common.phone")}</SrLabel>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography>{user?.phones[0]?.number ?? "-"}</Typography>
              </Grid>
              {user.verifiedPhone ? (
                <Grid
                  item
                  xs={12}
                  md={4}
                  container
                  className={classes.statusVerified}
                >
                  <Box mr={1} display="flex" alignItems="center">
                    <CheckCircleIcon className={classes.statusVerified} />
                  </Box>
                  <Typography>{translate("common.verified")}</Typography>
                </Grid>
              ) : (
                <Grid
                  item
                  xs={4}
                  container
                  className={classes.statusUnverified}
                >
                  <Box mr={1} display="flex" alignItems="center">
                    <CancelIcon className={classes.statusUnverified} />
                  </Box>
                  <Typography>{translate("common.unverified")}</Typography>
                  <Box ml={4}>
                    <Link
                      color="primary"
                      component={RouterLink}
                      variant="caption"
                      to={AUTH_ROOT_ROUTE + PHONE_VERIFICATION_ROUTE}
                    >
                      {translate(`account.security.validatePhone`)}
                    </Link>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {error && (
                <Grid item xs={12}>
                  <SrAlert severity="error">{error}</SrAlert>
                </Grid>
              )}
              <Grid container item justifyContent="flex-start">
                <SrButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {translate("common.saveChanges")}
                </SrButton>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  );
};

export default Security;
